import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { companyService } from '../../services';
import { Card, Button, Toast } from '../ds';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const inputStyle = {
  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
  border: '1px solid #D7DCE4', borderRadius: 8, fontSize: 13.5,
  fontFamily: FONT, outline: 'none', color: '#101623',
};
const disabledInputStyle = { ...inputStyle, background: '#F5F7FA', color: '#8A93A3', cursor: 'not-allowed' };
const labelStyle = { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 6 };
const fieldWrapStyle = { marginBottom: 14 };
const errorStyle = { fontSize: 12, color: '#C22B31', marginTop: 4 };

const PLZ_RE = /^\d{5}$/;

const Field = ({ label, children, error }) => (
  <div style={fieldWrapStyle}>
    <label style={labelStyle}>{label}</label>
    {children}
    {error && <div style={errorStyle}>{error}</div>}
  </div>
);

// ── Person-Karte ─────────────────────────────────────────────────────────
const PersonCard = ({ company, onSaved }) => {
  const { user } = useUser();
  const initial = {
    contactSalutation: company.contactSalutation || '',
    contactPhone: company.contactPhone || '',
    ownerPosition: company.ownerPosition || '',
  };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { setForm(initial); }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = Object.keys(initial).some(k => form[k] !== initial[k]);
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.ownerPosition.trim()) errs.ownerPosition = 'Bitte Rolle/Funktion angeben.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await companyService.updateMyCompany(form);
      onSaved(res.data);
      setToast({ tone: 'secure', text: 'Persönliche Daten gespeichert.' });
    } catch (err) {
      setToast({ tone: 'critical', text: 'Speichern fehlgeschlagen. Bitte erneut versuchen.' });
    } finally {
      setSaving(false);
    }
  };

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '–';

  return (
    <Card title="Person" subtitle="Ihre Kontaktdaten für Audits und Reports" padding={22}>
      <div className="sd-grid-2col">
        <Field label="Vorname / Nachname">
          <input value={displayName} disabled style={disabledInputStyle} />
        </Field>
        <Field label="Anrede">
          <select value={form.contactSalutation} onChange={set('contactSalutation')} style={inputStyle}>
            <option value="">Keine Angabe</option>
            <option value="Herr">Herr</option>
            <option value="Frau">Frau</option>
            <option value="Divers">Divers</option>
          </select>
        </Field>
        <Field label="Telefon">
          <input type="tel" inputMode="tel" autoComplete="tel" value={form.contactPhone} onChange={set('contactPhone')} placeholder="+49 …" style={inputStyle} />
        </Field>
        <Field label="Rolle / Funktion" error={errors.ownerPosition}>
          <select value={form.ownerPosition} onChange={set('ownerPosition')} style={inputStyle}>
            <option value="" disabled>Bitte wählen</option>
            <option value="Geschäftsführer">Geschäftsführer</option>
            <option value="IT-Verantwortlicher">IT-Verantwortlicher</option>
            {form.ownerPosition && !['Geschäftsführer', 'IT-Verantwortlicher'].includes(form.ownerPosition) && (
              <option value={form.ownerPosition}>{form.ownerPosition}</option>
            )}
          </select>
        </Field>
      </div>
      <p style={{ fontSize: 12, color: '#8A93A3', margin: '2px 0 16px' }}>
        Name und Login-E-Mail werden unter „Konto" verwaltet.
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button size="md" disabled={!dirty || saving} onClick={handleSave}>{saving ? 'Speichert…' : 'Speichern'}</Button>
        <Button variant="secondary" size="md" disabled={!dirty || saving} onClick={() => setForm(initial)}>Verwerfen</Button>
      </div>
      <Toast message={toast?.text} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </Card>
  );
};

// ── Unternehmen-Karte ────────────────────────────────────────────────────
const CompanyCard = ({ company, onSaved }) => {
  const initial = {
    name: company.name || '',
    legalForm: company.legalForm || '',
    street: company.street || '',
    zipCode: company.zipCode || '',
    city: company.city || '',
    state: company.state || '',
    country: company.country || 'Deutschland',
    industry: company.industry || '',
    employeeCount: company.employeeCount ?? '',
    itDeviceUserCount: company.itDeviceUserCount ?? '',
    vatId: company.vatId || '',
    commercialRegisterNumber: company.commercialRegisterNumber || '',
    wzCode: company.wzCode || '',
    additionalContacts: company.additionalContacts || '',
  };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { setForm(initial); }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = Object.keys(initial).some(k => String(form[k]) !== String(initial[k]));
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Bitte Firmenname angeben.';
    if (!form.street.trim()) errs.street = 'Bitte Straße und Hausnummer angeben.';
    if (!PLZ_RE.test(form.zipCode.trim())) errs.zipCode = 'Bitte eine gültige 5-stellige PLZ angeben.';
    if (!form.city.trim()) errs.city = 'Bitte Ort angeben.';
    if (!form.country.trim()) errs.country = 'Bitte Land angeben.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await companyService.updateMyCompany({
        ...form,
        employeeCount: form.employeeCount === '' ? null : parseInt(form.employeeCount, 10),
        itDeviceUserCount: form.itDeviceUserCount === '' ? null : parseInt(form.itDeviceUserCount, 10),
      });
      onSaved(res.data);
      setToast({ tone: 'secure', text: 'Unternehmensdaten gespeichert.' });
    } catch (err) {
      setToast({ tone: 'critical', text: 'Speichern fehlgeschlagen. Bitte erneut versuchen.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Unternehmen" subtitle="Erscheint u. a. im Dashboard-Header und in Audits" padding={22}>
      <div className="sd-grid-2col">
        <Field label="Firmenname" error={errors.name}>
          <input value={form.name} onChange={set('name')} style={inputStyle} />
        </Field>
        <Field label="Rechtsform">
          <input value={form.legalForm} onChange={set('legalForm')} placeholder="z. B. GmbH" style={inputStyle} />
        </Field>
        <Field label="Straße und Hausnummer" error={errors.street}>
          <input value={form.street} onChange={set('street')} style={inputStyle} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '35% 65%', gap: 10 }}>
          <Field label="PLZ" error={errors.zipCode}>
            <input value={form.zipCode} onChange={set('zipCode')} inputMode="numeric" pattern="[0-9]*" style={inputStyle} />
          </Field>
          <Field label="Ort" error={errors.city}>
            <input value={form.city} onChange={set('city')} style={inputStyle} />
          </Field>
        </div>
        <Field label="Bundesland">
          <input value={form.state} onChange={set('state')} placeholder="z. B. Bayern" style={inputStyle} />
        </Field>
        <Field label="Land" error={errors.country}>
          <input value={form.country} onChange={set('country')} style={inputStyle} />
        </Field>
        <Field label="Handelsregister-Nr.">
          <input value={form.commercialRegisterNumber} onChange={set('commercialRegisterNumber')} placeholder="falls vorhanden" style={inputStyle} />
        </Field>
        <Field label="USt-IdNr.">
          <input value={form.vatId} onChange={set('vatId')} placeholder="z. B. DE123456789" style={inputStyle} />
        </Field>
        <Field label="Branche">
          <input value={form.industry} onChange={set('industry')} placeholder="z. B. Einzelhandel" style={inputStyle} />
        </Field>
        <Field label="WZ-Code (WZ 2008)">
          <input value={form.wzCode} onChange={set('wzCode')} placeholder="z. B. 47.11.0" style={inputStyle} />
        </Field>
        <Field label="Beschäftigte gesamt">
          <input type="number" min="0" inputMode="numeric" value={form.employeeCount} onChange={set('employeeCount')} style={inputStyle} />
        </Field>
        <Field label="davon mit IT-Endgeräten">
          <input type="number" min="0" inputMode="numeric" value={form.itDeviceUserCount} onChange={set('itDeviceUserCount')} style={inputStyle} />
        </Field>
      </div>
      <div style={{ marginTop: 4 }}>
        <Field label="Weitere Personen/Dienstleister (Name + Rolle)">
          <textarea
            value={form.additionalContacts} onChange={set('additionalContacts')} rows={2}
            placeholder="z. B. Max Mustermann, externer IT-Dienstleister"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: FONT }}
          />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <Button size="md" disabled={!dirty || saving} onClick={handleSave}>{saving ? 'Speichert…' : 'Speichern'}</Button>
        <Button variant="secondary" size="md" disabled={!dirty || saving} onClick={() => setForm(initial)}>Verwerfen</Button>
      </div>
      <Toast message={toast?.text} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </Card>
  );
};

const StammdatenTab = ({ company, onCompanyChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <PersonCard company={company} onSaved={onCompanyChange} />
    <CompanyCard company={company} onSaved={onCompanyChange} />
  </div>
);

export default StammdatenTab;
